import React from 'react'
import SettingsFormContainer from 'src/Elements/SystemSettings/forms/SettingsFormContainer'
import { SingleToggleSettingBox, SingleToggleWithChildSettingsBox } from 'src/Elements/SystemSettings/forms'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { SmartPowerSettingsFormModel } from 'src/Libs/smart-power/models'
import { SettingsBox } from 'src/Elements/Data'
import { NumberField } from 'src/Libs/inputs/formik'
import { useField, useFormikContext } from 'formik'
import { SmartPowerValidationBoundaries } from '../types'
import { UnitTypes } from 'src/Libs/types'
import { ButtonGroupAction } from '@e3dc-react/shell/Types/component-types'

interface Props {
  model: SmartPowerSettingsFormModel
  isLoading?: boolean
  isReadOnlyAccess?: boolean
  formBoundaries: SmartPowerValidationBoundaries
}

const SmartPowerSettings: React.VFC<Props> = ({ model, isLoading, isReadOnlyAccess, formBoundaries }) => {
  const { t } = useCustomTranslation()

  const [emsPowerLimitsUsedField] = useField<SmartPowerSettingsFormModel>('emsPowerLimitsUsed')
  const { setFieldValue, setTouched } = useFormikContext()

  const { maxChargePower, maxDischargePower, maxStartDischargePower, minChargePower, minDischargePower, minStartDischargePower } = formBoundaries

  const powerLimitToggleActions: ButtonGroupAction[] = [
    {
      id: 'emsPowerLimitsUsed',
      label: t('systemSettings.smartFunctions.smartPower.settings.emsPowerLimitsUsed.option1'),
      onChange: () => {
        setFieldValue('emsPowerLimitsUsed', true)
        setTouched({
          emsMaxChargePower: true,
          emsMaxDischargePower: true,
          emsDischargeStartPower: true,
        })
      },
    },
    {
      id: 'Installation',
      label: t('systemSettings.smartFunctions.smartPower.settings.emsPowerLimitsUsed.option2'),
      onChange: () => {
        setFieldValue('emsPowerLimitsUsed', false)
      },
    },
  ]

  return (
    <SettingsFormContainer model={model} isLoading={isLoading}>
      <SingleToggleWithChildSettingsBox
        title={t('systemSettings.smartFunctions.smartPower.settings.emsPowerLimitsUsed.label')}
        description={t('systemSettings.smartFunctions.smartPower.settings.emsPowerLimitsUsed.description')}
        toggleActions={powerLimitToggleActions}
        activeActionIndex={emsPowerLimitsUsedField.value ? 0 : 1}
        disabled={isReadOnlyAccess}
      >
        {emsPowerLimitsUsedField.value ? (
          <>
            <SettingsBox
              title={t('systemSettings.smartFunctions.smartPower.settings.label.emsMaxChargePower', {
                min: minChargePower,
                max: maxChargePower,
                unit: UnitTypes.watt,
              })}
              suffix={<NumberField name="emsMaxChargePower" suffix={UnitTypes.watt} disabled={isReadOnlyAccess} />}
              disabled={isReadOnlyAccess}
              depth={1}
            />
            <SettingsBox
              title={t('systemSettings.smartFunctions.smartPower.settings.label.emsMaxDischargePower', {
                min: minDischargePower,
                max: maxDischargePower,
                unit: UnitTypes.watt,
              })}
              suffix={<NumberField name="emsMaxDischargePower" suffix={UnitTypes.watt} disabled={isReadOnlyAccess} />}
              disabled={isReadOnlyAccess}
              depth={1}
            />
            <SettingsBox
              title={t('systemSettings.smartFunctions.smartPower.settings.label.emsDischargeStartPower', {
                min: minStartDischargePower,
                max: maxStartDischargePower,
                unit: UnitTypes.watt,
              })}
              suffix={<NumberField name="emsDischargeStartPower" suffix={UnitTypes.watt} disabled={isReadOnlyAccess} />}
              disabled={isReadOnlyAccess}
              depth={1}
            />
          </>
        ) : undefined}
      </SingleToggleWithChildSettingsBox>

      <SingleToggleSettingBox
        title={t('systemSettings.smartFunctions.smartPower.settings.emsPowersaveEnabled.label')}
        description={t('systemSettings.smartFunctions.smartPower.settings.emsPowersaveEnabled.description')}
        name="emsPowersaveEnabled"
        disabled={isReadOnlyAccess}
        fullWidth
      />
    </SettingsFormContainer>
  )
}

export default SmartPowerSettings
